import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';

import classes from './ServicesReportTable.module.scss';
import { FC } from 'react';
import Skeleton from '../../../Skeleton/Skeleton';

type ServicesReportTableType = {
  date: string;
  serviceName: string;
  registredServices: string;
  calledFromQueue: string;
  served: string;
  redirected: string;
  missed: string;
  averageServiceTime: string;
};

const parseNumberLoose = (value: unknown) => {
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const parsed = Number(raw.replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const columnSizes = {
  dateSize: 200,
  serviceNameSize: 500,
  registredServicesSize: 200,
  calledFromQueueSize: 200,
  servedSize: 200,
  redirectedSize: 200,
  missedSize: 200
};

const {
  dateSize,
  serviceNameSize,
  registredServicesSize,
  calledFromQueueSize,
  servedSize,
  redirectedSize,
  missedSize
} = columnSizes;

type ServicesReportTableProps = {
  serviceReportData: ServicesReportTableType[];
  isLoadingData: boolean;
};

const ServicesReportTable: FC<ServicesReportTableProps> = ({
  serviceReportData,
  isLoadingData
}) => {
  const columnHelper = createColumnHelper<ServicesReportTableType>();

  const categoriesColumns = [
    columnHelper.accessor('date', {
      header: () => <div className={classes.headerContent}>Дата</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: dateSize
    }),
    columnHelper.accessor('serviceName', {
      header: () => <div className={classes.headerContent}>Наименование услуги</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: serviceNameSize
    }),
    columnHelper.accessor('registredServices', {
      header: () => <div className={classes.headerContent}>Зарегистрировано</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: registredServicesSize
    }),
    columnHelper.accessor('calledFromQueue', {
      header: () => <div className={classes.headerContent}>Вызвано из очереди</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: calledFromQueueSize
    }),
    columnHelper.accessor('served', {
      header: () => <div className={classes.headerContent}>Обслужено</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: servedSize
    }),
    columnHelper.accessor('redirected', {
      header: () => <div className={classes.headerContent}>Перенаправлено</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: redirectedSize
    }),
    columnHelper.accessor('missed', {
      header: () => <div className={classes.headerContent}>Не пришли</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: missedSize
    })
  ];

  const columns = categoriesColumns;
  const [activeRow, setActiveRow] = React.useState(null);

  const handleClick = (id: string) => {
    setActiveRow(id);
  };

  const tableData = React.useMemo(
    () => (isLoadingData ? Array(5).fill({}) : serviceReportData || []),
    [isLoadingData, serviceReportData]
  );
  const tableColumns = React.useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [isLoadingData, columns]
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel()
  });

  const totals = React.useMemo(() => {
    if (isLoadingData) return null;
    const rows = serviceReportData ?? [];
    const totalRegistered = rows.reduce((acc, r) => acc + parseNumberLoose(r.registredServices), 0);
    const totalCalled = rows.reduce((acc, r) => acc + parseNumberLoose(r.calledFromQueue), 0);
    const totalServed = rows.reduce((acc, r) => acc + parseNumberLoose(r.served), 0);
    const totalRedirected = rows.reduce((acc, r) => acc + parseNumberLoose(r.redirected), 0);
    const totalMissed = rows.reduce((acc, r) => acc + parseNumberLoose(r.missed), 0);

    return {
      totalRegistered,
      totalCalled,
      totalServed,
      totalRedirected,
      totalMissed
    };
  }, [isLoadingData, serviceReportData]);

  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: `${header.getSize()}px` }}>
                    <div className={classes.thTitle}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          className: `resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`
                        }}>
                        <div className={classes.colorResizer} />
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => handleClick(row.id)}
              className={activeRow === row.id ? classes.activeRow : ''}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {!isLoadingData && totals && (
          <tfoot>
            <tr className={classes.totalRow}>
              <td className={classes.totalCell}>Итого</td>
              <td className={classes.totalCell} />
              <td className={classes.totalCell}>{totals.totalRegistered}</td>
              <td className={classes.totalCell}>{totals.totalCalled}</td>
              <td className={classes.totalCell}>{totals.totalServed}</td>
              <td className={classes.totalCell}>{totals.totalRedirected}</td>
              <td className={classes.totalCell}>{totals.totalMissed}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default ServicesReportTable;
